import { useState, useEffect, useRef } from 'react';
import { Loader2, Save } from 'lucide-react';
import { TabSkeleton } from "../BotManagement/BotManagementSkeleton";
import { useParams } from 'react-router-dom';
import { authService } from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { MediaManager } from '../../../components/MediaManager/MediaManager';
import TextForm from '../../../components/TextForm/TextForm';
import PushTip from '../../../components/UI/PushTip/PushTip'
import MarketIcon from '../../../components/Flags/MarketIcon';
import { normalizeMode } from '../../../utils/market';
import MessagesEditor from './MessagesEditor';
import SocialProofEditor from './SocialProofEditor';

const NO_PROOF = { enabled: false, list: [''], interval: 30 };
// Prova social de um mercado vinda da API → estado do editor.
const proofFromApi = (cfg) => (cfg ? { enabled: !!cfg.enabled, list: cfg.list?.length ? cfg.list : [''], interval: cfg.interval || 30 } : NO_PROOF);
// Estado do editor → API (sem linhas vazias).
const proofToApi = (p) => ({ enabled: p.enabled, list: p.list.filter((t) => t.trim()), interval: Number(p.interval) || 30 });

// Mercados da aba: Brasil (PIX) e os textos das telas do Global e do LATAM.
const MARKET_TABS = [
    { mode: 'brl', label: 'Brasil' },
    { mode: 'usd', label: 'Global' },
    { mode: 'mxn', label: 'LATAM' },
];

export default function PixConfig() {
    const { botId } = useParams();
    const { showError, showSuccess } = useNotification();
    const mediaRef = useRef();
    // Mídias antes do pagamento no Global e no LATAM (uma lista por mercado).
    const marketMediaRefs = { usd: useRef(), mxn: useRef() };

    const [loading, setLoading] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    // Mercado aberto na aba (começa no do bot) e textos do Global/LATAM.
    const [market, setMarket] = useState('brl');
    const [cardTexts, setCardTexts] = useState({ usd: {}, mxn: {} });
    const [textDefaults, setTextDefaults] = useState({ usd: {}, mxn: {} });

    // Mensagem PIX
    const [defaultTemplate, setDefaultTemplate] = useState('');
    const [template, setTemplate] = useState('');
    const [codeFormat, setCodeFormat] = useState('blockquote');

    // Mídias
    const [mediaList, setMediaList] = useState([]);
    const [sendMode, setSendMode] = useState('agrupado');

    // Botões
    const [showVerify, setShowVerify] = useState(true);
    const [showCopy, setShowCopy] = useState(true);
    const [showQr, setShowQr] = useState(true);
    const [verifyText, setVerifyText] = useState('');
    const [copyText, setCopyText] = useState('');
    const [qrText, setQrText] = useState('');
    const [showQrCode, setShowQrCode] = useState(true);

    // Prova Social: a do PIX (Brasil) e a de cada mercado (Global, LATAM)
    const [pixProof, setPixProof] = useState(NO_PROOF);
    const [marketProof, setMarketProof] = useState({ usd: NO_PROOF, mxn: NO_PROOF });

    // Global/LATAM: mídias (null no LATAM = ainda usa as do PIX) e mensagens do SPEI/Nequi.
    const [marketMedia, setMarketMedia] = useState({ usd: { list: [], send_mode: 'agrupado' }, mxn: { list: null, send_mode: 'agrupado' } });
    const [localTemplates, setLocalTemplates] = useState({ spei: '', nequi: '' });
    const [localDefaults, setLocalDefaults] = useState({ spei: '', nequi: '' });
    const [buttonsOff, setButtonsOff] = useState([]);
    // Aba de mensagem a abrir (ex.: SPEI recusado ao salvar).
    const [messageFocus, setMessageFocus] = useState(null);

    useEffect(() => {
        if (!botId) return;
        loadData();
    }, [botId]);

    const loadData = async () => {
        try {
            setLoadingPage(true);
            const { data } = await authService.bots.edit_bot(botId);
            const pc = data.bot?.pix_config || {};

            setMarket(normalizeMode(pc.currency_mode));
            setCardTexts({ usd: pc.card_texts?.usd || {}, mxn: pc.card_texts?.mxn || {} });
            setTextDefaults({ usd: pc.card_text_defaults?.usd || {}, mxn: pc.card_text_defaults?.mxn || {} });
            setDefaultTemplate(pc.default_template || '');
            setTemplate(pc.message_template || '');
            setCodeFormat(pc.code_format || 'blockquote');
            setMediaList(pc.media_list || []);
            setSendMode(pc.media_send_mode || 'agrupado');
            setShowVerify(pc.show_verify_button !== false);
            setShowCopy(pc.show_copy_button !== false);
            setShowQr(pc.show_qr_button !== false);
            setVerifyText(pc.verify_button_text || '');
            setCopyText(pc.copy_button_text || '');
            setQrText(pc.qr_button_text || '');
            setPixProof({
                enabled: !!pc.social_proof_enabled,
                list: pc.social_proof_list?.length ? pc.social_proof_list : [''],
                interval: pc.social_proof_interval || 30,
            });
            setMarketProof({ usd: proofFromApi(pc.market_social_proof?.usd), mxn: proofFromApi(pc.market_social_proof?.mxn) });
            setMarketMedia({
                usd: { list: pc.market_media?.usd?.list || [], send_mode: pc.market_media?.usd?.send_mode || 'agrupado' },
                mxn: { list: pc.market_media?.mxn?.list ?? null, send_mode: pc.market_media?.mxn?.send_mode || 'agrupado' },
            });
            const defaults = pc.local_template_defaults || {};
            setLocalDefaults({ spei: defaults.spei || '', nequi: defaults.nequi || '' });
            // Sem modelo próprio, o editor começa no padrão (edita a partir dele).
            setButtonsOff(pc.local_buttons_off || []);
            setLocalTemplates({
                spei: pc.local_templates?.spei || defaults.spei || '',
                nequi: pc.local_templates?.nequi || defaults.nequi || '',
            });
        } catch (err) {
            showError('Erro ao carregar configuração PIX');
        } finally {
            setLoadingPage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // {pix_code} é obrigatório no template
        if (template.trim() && !template.includes('{pix_code}')) {
            showError('O template deve conter a variável {pix_code}');
            return;
        }
        if (localTemplates.spei.trim() && !localTemplates.spei.includes('{clabe}')) {
            setMarket('mxn');
            setMessageFocus('spei');
            showError('A mensagem do SPEI deve conter a variável {clabe}');
            return;
        }

        setLoading(true);
        try {
            const pix = proofToApi(pixProof);
            await authService.bots.save_pix_config(botId, {
                card_texts: cardTexts,
                // Igual ao padrão = sem modelo próprio (acompanha melhorias do padrão).
                local_templates: {
                    spei: localTemplates.spei.trim() === localDefaults.spei.trim() ? '' : localTemplates.spei,
                    nequi: localTemplates.nequi.trim() === localDefaults.nequi.trim() ? '' : localTemplates.nequi,
                },
                local_buttons_off: buttonsOff,
                market_media_send_mode: { usd: marketMedia.usd.send_mode, mxn: marketMedia.mxn.send_mode },
                message_template: template.trim() || null,
                media_send_mode: sendMode,
                code_format: codeFormat,
                show_verify_button: showVerify,
                show_copy_button: showCopy,
                show_qr_button: showQr,
                verify_button_text: verifyText.trim() || null,
                copy_button_text: copyText.trim() || null,
                qr_button_text: qrText.trim() || null,
                social_proof_enabled: pix.enabled,
                social_proof_list: pix.list,
                social_proof_interval: pix.interval,
                market_social_proof: { usd: proofToApi(marketProof.usd), mxn: proofToApi(marketProof.mxn) },
                show_qr_code: showQrCode,
            });

            for (const ref of [mediaRef, marketMediaRefs.usd, marketMediaRefs.mxn]) {
                if (ref.current) await ref.current.uploadAll();
            }

            showSuccess('Pagamentos salvos!');
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Erro ao salvar';
            showError(msg);
        } finally {
            setLoading(false);
        }
    };


    if (loadingPage) {
        return <TabSkeleton tab="pix" />;
    }

    return (
        <form className="spt-reveal" onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <div className="spt-form-bot">

                {/* MERCADO: Brasil (PIX) ou os textos das telas do Global/LATAM */}
                <div role="tablist" aria-label="Mercado" className="inline-flex items-center gap-0.5 p-0.5 mb-6 rounded-lg border border-white/10 bg-white/[0.03]">
                    {MARKET_TABS.map((t) => (
                        <button key={t.mode} type="button" role="tab" aria-selected={market === t.mode}
                            onClick={() => setMarket(t.mode)}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                market === t.mode ? 'bg-white/10 text-white ring-1 ring-white/20' : 'text-white/55 hover:text-white hover:bg-white/[0.05]'
                            }`}>
                            <MarketIcon mode={t.mode} />
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Global e LATAM ficam montados (escondidos) como o Brasil: mídias
                    escolhidas e ainda não enviadas não se perdem ao trocar de aba. */}
                {['usd', 'mxn'].map((m) => (
                    <div key={m} hidden={market !== m}>
                        <p className="spt-hint mb-4">
                            {m === 'usd'
                                ? 'Pagamento no modo Global (comprador em inglês).'
                                : 'Pagamento no modo LATAM (comprador em espanhol).'}
                        </p>

                        <section>
                            <h3 className="spt-label">Mídias do pagamento</h3>
                            <p className="spt-hint">
                                Enviadas antes da mensagem de pagamento.
                                {m === 'mxn' && marketMedia.mxn.list === null && ' Sem mídias próprias, o LATAM usa as mídias do PIX.'}
                            </p>
                            <MediaManager
                                ref={marketMediaRefs[m]}
                                botId={botId}
                                targetType="bot"
                                targetId={botId}
                                multiMedia={true}
                                mediaList={marketMedia[m].list || []}
                                onMediaChange={(list) => setMarketMedia((prev) => ({ ...prev, [m]: { ...prev[m], list } }))}
                                sendMode={marketMedia[m].send_mode}
                                onSendModeChange={(mode) => setMarketMedia((prev) => ({ ...prev, [m]: { ...prev[m], send_mode: mode } }))}
                                slotOverride={m === 'usd' ? 'global_multi' : 'latam_multi'}
                                mediaLabel={m === 'usd' ? 'do Global' : 'do LATAM'}
                            />
                        </section>

                        <div className="mt-8 flex flex-col gap-4">
                            <div>
                                <h3 className="spt-label mb-0.5">Mensagens</h3>
                                <p className="spt-hint">Na ordem em que o comprador vê. Campo vazio = texto padrão (o da prévia).</p>
                            </div>
                            <MessagesEditor
                                market={m}
                                templates={localTemplates} templateDefaults={localDefaults} onTemplatesChange={setLocalTemplates}
                                texts={cardTexts[m]} textDefaults={textDefaults[m]}
                                onTextsChange={(texts) => setCardTexts((prev) => ({ ...prev, [m]: texts }))}
                                buttonsOff={buttonsOff} onButtonsOffChange={setButtonsOff}
                                focus={m === 'mxn' ? messageFocus : null}
                            />
                        </div>

                        <div className="mt-8">
                            <SocialProofEditor
                                market={m === 'usd' ? 'Global' : 'LATAM'}
                                value={marketProof[m]}
                                onChange={(proof) => setMarketProof((prev) => ({ ...prev, [m]: proof }))}
                                placeholder={m === 'usd' ? 'Social proof' : 'Prueba social'}
                                hint={`Após o pagamento ser gerado, uma mensagem cicla entre os textos abaixo (máx. 5). Escreva ${m === 'usd' ? 'em inglês' : 'em espanhol'}, no idioma do comprador.`}
                            />
                        </div>
                    </div>
                ))}

                {/* Brasil fica montado (escondido) para não perder mídias ainda não enviadas. */}
                <div hidden={market !== 'brl'}>
                {/* MÍDIAS PIX */}
                <div className="mt-8">
                    <h3 className="spt-label">Mídias do PIX</h3>
                    <p className="spt-hint">Enviadas antes da mensagem de pagamento.</p>
                    <MediaManager
                        ref={mediaRef}
                        botId={botId}
                        targetType="bot"
                        targetId={botId}
                        multiMedia={true}
                        mediaList={mediaList}
                        onMediaChange={setMediaList}
                        sendMode={sendMode}
                        onSendModeChange={setSendMode}
                        slotOverride="pix_multi"
                        mediaLabel="do PIX"
                    />
                </div>

                {/* MENSAGEM PIX */}
                <div className="mt-8">
                    <h3 className="spt-label">
                        Mensagem PIX
                        <span className="required">*</span>
                    </h3>



                    <TextForm
                        value={template}
                        onChange={setTemplate}
                        variables={['plano', 'duration', 'valor', 'pix_code']}
                    />
                    <PushTip>A variável <strong>{'{pix_code}'}</strong> é obrigatória quando o template for preenchido.</PushTip>



                </div>

                {/* FORMATO DO CÓDIGO */}
                <div className="mt-6">
                    <label className="spt-label">Formato do código PIX</label>
                    <select
                        className="form-select"
                        value={codeFormat}
                        onChange={e => setCodeFormat(e.target.value)}
                    >
                        <option value="blockquote">Blockquote (padrão)</option>
                        <option value="text">Texto inline (monospace)</option>
                        <option value="code_block">Bloco de código</option>
                    </select>
                </div>

                {/* BOTÕES */}
                <div className="mt-8">
                    <h3 className="spt-label mb-2">Botões</h3>

                    <div className="flex flex-col gap-4">
                        <ToggleRow
                            label="Verificar Pagamento"
                            checked={showVerify}
                            onToggle={setShowVerify}
                            textValue={verifyText}
                            onTextChange={setVerifyText}
                            placeholder="🔄Verificar Pagamento"
                        />
                        <ToggleRow
                            label="Copiar Código"
                            checked={showCopy}
                            onToggle={setShowCopy}
                            textValue={copyText}
                            onTextChange={setCopyText}
                            placeholder="📋 Copiar Código"
                        />
                        <ToggleRow
                            label="Ver QR Code"
                            checked={showQr}
                            onToggle={setShowQr}
                            textValue={qrText}
                            onTextChange={setQrText}
                            placeholder="📱Ver QR Code"
                        />
                    </div>
                </div>

                {/* PROVA SOCIAL */}
                <div className="mt-8">
                    <SocialProofEditor
                        value={pixProof}
                        onChange={setPixProof}
                        hint="Após o PIX ser gerado, uma mensagem é enviada e vai ciclando automaticamente entre os textos abaixo (máx. 5), editando a mesma mensagem."
                    />
                </div>

                {/* QRCODE */}
                <div className="mt-8 flex items-center justify-between mb-2">
                    <h3 className="spt-label mb-0">Mostrar QR Code</h3>
                    <label className="relative inline-flex items-center cursor-pointer">

                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={showQrCode}
                            onChange={e => setShowQrCode(e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500" />
                    </label>
                </div>

                </div>

                {/* SALVAR */}
                <div className="flex justify-end mt-12">
                    <button disabled={loading} className="btn-primary">
                        {loading ? (
                            <><Loader2 size={14} className="animate-spin me-2" />Salvando...</>
                        ) : (
                            <><Save size={14} className="me-2" />Salvar pagamentos</>
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
}

function ToggleRow({ label, checked, onToggle, textValue, onTextChange, placeholder }) {
    return (
        <div className="flex  gap-2 p-3 rounded-lg border border-white/10">
            <div className="flex items-center justify-between">
                {/* <span className="text-sm font-medium">{label}</span> */}
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={checked}
                        onChange={e => onToggle(e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500" />
                </label>
            </div>

            <input
                type="text"
                className="input3"
                placeholder={placeholder}
                value={textValue}
                onChange={e => onTextChange(e.target.value)}
            />

        </div>
    );
}
